import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import type {
  Grid,
  GridColumnProps,
  GridColumnResizeEvent,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridPagerSettings,
} from "@progress/kendo-vue-grid";
import { computed, ref, type Ref } from "vue";
import { GridOptionsService } from "./GridOptionsService";
import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { SortDescriptor } from "@progress/kendo-data-query";

export interface GridEmits {
  (e: "itemsChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
}

export default class GridController<T> {
  static ADJUST_PADDING = 4;
  static COLUMN_MIN = 4;

  columns = ref([] as GridColumnPropsExtended[]);
  pageSizes = ref([10, 20, 50, 100]);
  gridName = "DefaultGrid";
  skip = ref(0);
  take = ref(10);
  pageable = ref<GridPagerSettings>({
    buttonCount: 5,
    info: true,
    type: "numeric",
    pageSizes: this.pageSizes.value,
    previousNext: true,
  });
  emit = ((e: any, options: any) => {}) as GridEmits;

  gridRef: Ref<InstanceType<typeof Grid> | null> = ref(null);
  private setMinWidth = false;
  private offsetWidth = 0;
  private defaultColWidths = new Map<string, string | number | undefined>();
  totalMinWidth = computed(() =>
    this.columns.value
      .filter((c) => !c.hidden)
      .reduce((a, b) => a + (b.minWidth ?? 0), 0)
  );

  constructor(
    gridName: string,
    columns: GridColumnPropsExtended[],
    emits?: GridEmits,
    take?: number
  ) {
    this.gridName = gridName;
    this.columns.value = columns;
    if (emits) this.emit = emits;

    if (take) {
      this.addPageSize(take);
      this.take.value = take;
    }
  }

  setGridRef(gridRef: Ref<InstanceType<typeof Grid> | null>) {
    if (!gridRef.value) return;

    this.gridRef = gridRef;
    this.offsetWidth = (gridRef.value as any).offsetWidth;
    this.setMinWidth =
      (gridRef.value as any).offsetWidth < this.totalMinWidth.value;
    this.defaultColWidths = new Map(
      this.columns.value.map((column, ind) => [
        column.title ?? ind.toString(),
        column.width,
      ])
    );
    this.handleResize(true);
    window.addEventListener("resize", () => this.handleResize());
  }

  initOptions() {
    GridOptionsService.loadGridOptions(this.gridName, this.columns.value);
  }

  addPageSize(pageSize: number) {
    if (this.pageSizes.value.includes(pageSize)) return;
    this.pageSizes.value.push(pageSize);
    this.pageSizes.value = this.pageSizes.value.sort((a, b) => a - b);
  }

  filterChangeHandler(e: GridFilterChangeEvent) {
    this.skip.value = 0;
    this.emit("itemsChanged", {
      skip: 0,
      take: this.take.value,
      filterEvent: e,
    });
  }

  sortChangeHandler(e: any) {
    const descriptors = e.sort as SortDescriptor[];
    if (!descriptors) return;
    this.skip.value = 0;
    this.emit("sortChanged", {
      field: descriptors[0].field,
      ascending: descriptors[0].dir === "asc",
    });
  }

  pageChangeHandler(event: GridPageChangeEvent) {
    this.skip.value = event.page.skip;
    this.take.value = event.page.take;
    this.emit("itemsChanged", {
      skip: event.page.skip,
      take: event.page.take,
      filterEvent: null,
    });
  }

  onColumnsSubmit(columnsProps: GridColumnProps[]) {
    if (!columnsProps) return;

    this.columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(this.gridName, columnsProps);

    if (!this.gridRef.value) return;
    this.handleResize();
  }

  onColumnResize(e: GridColumnResizeEvent) {
    const col = GridOptionsService.getResizedColumn(e, this.columns.value);
    if (col.title)
      GridOptionsService.saveColumnWidth(this.gridName, col.title, e.newWidth);
  }

  // Setting a Minimum Column Width
  // https://www.telerik.com/kendo-vue-ui/components/grid/columns/width/
  handleResize(initialResize = false) {
    if (!this.gridRef?.value) return;

    const gridOffsetWidth =
      (this.gridRef.value as any).offsetWidth ??
      (this.gridRef.value as any)._element.offsetWidth;
    if (gridOffsetWidth < this.totalMinWidth.value && !this.setMinWidth) {
      this.setMinWidth = true;
    } else if (gridOffsetWidth > this.totalMinWidth.value) {
      this.offsetWidth = gridOffsetWidth;
      this.setMinWidth = false;
    }

    // Don't resize initially if total grid width is more than total columns min width
    if (initialResize && !this.setMinWidth) return;

    this.defineColumnWidths();
  }

  private defineColumnWidths() {
    this.columns.value.forEach((column) => {
      if (!column.minWidth) return;
      column.width = this.setWidth(column);
      if (!column.width) delete column.width;
    });
  }

  private getDefaultColumnWidth(column: GridColumnPropsExtended) {
    const defaultWidth = this.defaultColWidths.get(
      column.title ?? this.columns.value.indexOf(column).toString()
    );
    if (defaultWidth) return defaultWidth;
    return "";
  }

  private setWidth(column: GridColumnPropsExtended) {
    if (!this.setMinWidth) {
      const defaultWidth = this.getDefaultColumnWidth(column);
      if (defaultWidth) return defaultWidth;

      const visibleColumnDefaultWidths = this.columns.value
        .filter((c) => !c.hidden)
        .map((c) => this.getDefaultColumnWidth(c));
      const totalDefaultWidth = visibleColumnDefaultWidths.reduce((a, w) => {
        if (!w) return a;
        if (typeof w === "string") return (a as number) + parseInt(w);
        return (a as number) + (w as number);
      }, 0) as number;
      const emptyDefaultWidthCount = visibleColumnDefaultWidths.filter(
        (w) => !w
      ).length;
      return (
        (this.offsetWidth - totalDefaultWidth) / emptyDefaultWidthCount -
        GridController.ADJUST_PADDING
      );
    }

    let width = column.minWidth ?? 0;
    if (width >= GridController.COLUMN_MIN)
      width -= GridController.ADJUST_PADDING;
    return width;
  }
}
